import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/status";
import NewOrgDialog from "@/components/NewOrgDialog";
import SuspendOrgDialog from "@/components/SuspendOrgDialog";
import SetupProgress from "@/components/SetupProgress";

const STATUS = { active: "bg-emerald-50 text-emerald-800 border-emerald-200", suspended: "bg-red-50 text-red-800 border-red-200" };

function OrgRow({ o, onSuspend, onReactivate }) {
  return (
    <TableRow data-testid={`org-row-${o.id}`}>
      <TableCell><div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: o.color }} /><b>{o.name}</b></div>
        <div className="text-xs text-slate-500">{o.legal_entity}</div></TableCell>
      <TableCell><span className={`text-xs px-2 py-0.5 rounded border capitalize ${STATUS[o.lifecycle] || "bg-slate-50"}`} data-testid={`org-status-${o.id}`}>{o.lifecycle}</span>
        {o.suspension && <div className="text-xs text-red-700 mt-1 max-w-[220px]" data-testid={`org-suspension-${o.id}`}>{o.suspension.reason} - {o.suspension.by_name}, {fmt(o.suspension.at)}</div>}</TableCell>
      <TableCell><SetupProgress o={o} /></TableCell>
      <TableCell className="text-xs">{o.admins.join(", ") || <span className="text-amber-700">No administrator</span>}</TableCell>
      <TableCell className="text-sm tabular-nums">{o.members}</TableCell>
      <TableCell className="text-sm tabular-nums">{o.patients}</TableCell>
      <TableCell className="text-sm tabular-nums">{o.packets}</TableCell>
      <TableCell className="text-right">
        {o.lifecycle === "active" && <Button size="sm" variant="outline" className="text-red-700" onClick={() => onSuspend(o)} data-testid={`org-suspend-${o.id}`}>Suspend</Button>}
        {o.lifecycle === "suspended" && <Button size="sm" variant="outline" onClick={() => onReactivate(o)} data-testid={`org-reactivate-${o.id}`}>Reactivate</Button>}
      </TableCell>
    </TableRow>
  );
}

export default function OrgMaster({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [suspending, setSuspending] = useState(null);
  const load = () => api.get("/enterprise/organizations").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const done = () => { load(); onChanged?.(); };
  const reactivate = async (o) => {
    if (!window.confirm(`Reactivate ${o.name}? Staff can sign in to it again and its patient links work again.`)) return;
    try { await api.post(`/enterprise/organizations/${o.id}/reactivate`); toast.success(`${o.name} reactivated`); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rows.filter((o) => `${o.name} ${o.legal_entity}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3" data-testid="org-master">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative max-w-sm flex-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organizations" className="pl-8 h-9" data-testid="org-search" /></div>
        <Button className="btn-brand ml-auto" onClick={() => setCreating(true)} data-testid="org-new-btn"><Plus className="w-4 h-4 mr-1" />New organization</Button>
      </div>
      <div className="overflow-x-auto"><Table className="bg-white border rounded-xl"><TableHeader><TableRow><TableHead>Organization</TableHead><TableHead>Status</TableHead><TableHead>Setup</TableHead><TableHead>Administrators</TableHead>
        <TableHead>Staff</TableHead><TableHead>Patients</TableHead><TableHead>Packets</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>{shown.map((o) => <OrgRow key={o.id} o={o} onSuspend={setSuspending} onReactivate={reactivate} />)}</TableBody></Table></div>
      <NewOrgDialog open={creating} onOpenChange={setCreating} onCreated={done} />
      <SuspendOrgDialog org={suspending} onClose={() => setSuspending(null)} onDone={done} />
    </div>
  );
}
